import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Check, Loader2, DollarSign, CopyIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOLLARS = (c) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Certificate order confirmation / status page. Public route:
 * /certificates/order/:public_order_number?t=<status_token>
 *
 * The `t` token is required for guest orders — signed-in owners see
 * private details without a token.
 */
export default function CertificateOrderStatus() {
  const { public_order_number } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [order, setOrder] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);
  const [zelle, setZelle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const refetch = () => {
    const q = token ? `?t=${encodeURIComponent(token)}` : "";
    return axios.get(`${API}/certificate-orders/${public_order_number}${q}`,
                        { withCredentials: true }).then(r => setOrder(r.data.order));
  };
  useEffect(() => {
    refetch().catch(() => setOrder(null)).finally(() => setLoading(false));
    axios.get(`${API}/settings/public`).then(r => {
      setWhatsapp(r.data?.whatsapp_business_number || null);
    }).catch(() => {});
    axios.get(`${API}/orders-payment-instructions`).then(r => {
      setZelle(r.data || null);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [public_order_number, token]);

  const markPaid = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const q = token ? `?t=${encodeURIComponent(token)}` : "";
      await axios.post(`${API}/certificate-orders/${public_order_number}/payment-sent${q}`,
                          {}, { withCredentials: true });
      toast.success("Thanks — payment marked as sent. Gil will verify shortly.");
      await refetch();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not mark payment sent.");
    } finally { setSubmitting(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied."); };

  if (loading) {
    return <div className="container-gm py-16 flex items-center gap-3 text-[var(--smoke)]"><Loader2 className="animate-spin"/> Loading order…</div>;
  }
  if (!order) {
    return <div className="container-gm py-16">Order not found or not accessible without a valid token.</div>;
  }

  const c = order.customer || {};
  const ship = order.shipping_address || {};
  const alb = order.album || {};
  const pricing = order.pricing || {};
  const albumTitle = alb.album_title || `${alb.year} ${alb.host_country}`;
  const firstName = c.first_name || "Collector";
  const lastName = c.last_name || "";

  const waMsg = [
    "Hi Gil! I just placed my Album Complete Certificate order on CollectorUp.",
    `Order #: ${order.public_order_number}`,
    `Name: ${firstName}${lastName ? " " + lastName : ""}`,
    `Album: ${albumTitle}`,
    "I am sending my album-verification video next.",
  ].join("\n");
  const waHref = whatsapp
    ? `https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(waMsg)}`
    : null;

  return (
    <div className="container-gm py-8 md:py-12" data-testid="cert-order-status">
      <div className="mb-6 flex items-center gap-2 rounded-2xl bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] p-4">
        <Check size={22} className="text-[color:var(--field-green)]" />
        <div>
          <div className="font-serif text-2xl md:text-3xl">Thanks, {firstName}!</div>
          <div className="text-sm text-[var(--ink)]/80">
            Order <strong data-testid="cert-status-order-number">{order.public_order_number}</strong> received.
            Your certificate will not be prepared or shipped until your
            album-verification video is reviewed and approved.
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          {/* Zelle payment instructions — real supported payment path. */}
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-5 md:p-6" data-testid="cert-status-zelle-card">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={18} className="text-[color:var(--field-green)]" />
              <h2 className="font-serif text-2xl">Step 1 — send $10.70 by Zelle</h2>
            </div>
            {order.payment_status === "verified" ? (
              <div className="text-sm text-[color:var(--field-green)] font-bold">
                Payment verified — thanks!
              </div>
            ) : order.payment_status === "payment_needs_review" ? (
              <div className="rounded-2xl bg-[var(--panini-yellow-soft)] p-3 text-sm font-bold" data-testid="cert-status-payment-under-review">
                Payment marked as sent. Gil is reviewing it — you&apos;ll see this update to Verified once confirmed.
              </div>
            ) : (
              <>
                <p className="text-sm text-[var(--ink)]/80" data-testid="cert-status-zelle-instructions">
                  Send <strong>{DOLLARS(order.pricing?.total_cents ?? 1070)}</strong> by Zelle and include order
                  <button type="button" onClick={() => copy(order.public_order_number)}
                             className="mx-1 font-mono font-bold underline inline-flex items-center gap-1"
                             data-testid="cert-status-copy-order">
                    {order.public_order_number} <CopyIcon size={12}/>
                  </button>
                  in the memo.
                </p>
                {zelle && (zelle.zelle_recipient_email || zelle.zelle_recipient_phone) && (
                  <div className="mt-3 text-xs text-[var(--smoke)] space-y-1" data-testid="cert-status-zelle-recipient">
                    {zelle.zelle_recipient_name && <div>Recipient: <strong className="text-[var(--ink)]">{zelle.zelle_recipient_name}</strong></div>}
                    {zelle.zelle_recipient_email && <div>Zelle email: <strong className="text-[var(--ink)]">{zelle.zelle_recipient_email}</strong></div>}
                    {zelle.zelle_recipient_phone && <div>Zelle phone: <strong className="text-[var(--ink)]">{zelle.zelle_recipient_phone}</strong></div>}
                  </div>
                )}
                <button
                  data-testid="cert-status-mark-payment-sent"
                  onClick={markPaid}
                  disabled={submitting}
                  className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-[var(--panini-red)] text-white font-bold px-5 py-3 disabled:opacity-60"
                >
                  {submitting ? "Marking…" : "I sent my Zelle payment"}
                </button>
                <p className="text-[11px] text-[var(--smoke)] mt-2">
                  This does not verify the payment. Gil will review it and mark it verified after Zelle receipt.
                </p>
              </>
            )}
          </div>

          {/* WhatsApp CTA — the star of the page */}
          <div className="rounded-3xl border-4 border-[var(--panini-red)] bg-white p-5 md:p-6" data-testid="cert-status-whatsapp-card">
            <h2 className="font-serif text-2xl">Step 2 — send your verification video on WhatsApp</h2>
            <p className="text-sm text-[var(--ink)]/80 mt-2">
              Send the prefilled text first, then attach your video. Keep it
              under <strong>180 seconds</strong>, state your <strong>full name</strong>
              and <strong>today&apos;s date</strong>, show the <strong>front of the album</strong>
              and every album page clearly.
            </p>
            {waHref ? (
              <a
                data-testid="cert-status-whatsapp-btn"
                data-order-number={order.public_order_number}
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-[color:var(--field-green)] text-white font-bold px-5 py-3"
              >
                <MessageCircle size={18}/> Send verification video on WhatsApp
              </a>
            ) : (
              <div className="mt-4 text-sm text-[var(--panini-red)]">
                WhatsApp business number not configured. Contact support.
              </div>
            )}
          </div>

          {/* Statuses */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cert-status-badges">
            <StatusPill label="Payment" value={order.payment_status} testid="cert-status-payment" />
            <StatusPill label="Verification" value={order.verification_status} testid="cert-status-verification" />
            <StatusPill label="Fulfillment" value={order.fulfillment_status} testid="cert-status-fulfillment" />
          </div>

          {order.customer_note && (
            <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 text-sm" data-testid="cert-status-customer-note">
              <div className="kicker kicker-black text-[11px] mb-1">Note from Gil</div>
              {order.customer_note}
            </div>
          )}

          {order.tracking_number && (
            <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 text-sm" data-testid="cert-status-tracking">
              <div className="kicker kicker-black text-[11px] mb-1">Tracking</div>
              {order.tracking_number}
            </div>
          )}

          {order.final_certificate_number && (
            <Link
              data-testid="cert-status-final-cert-link"
              to={order.final_certificate_url || `/certificates/${order.final_certificate_number}`}
              className="block rounded-2xl bg-[var(--collector-gold)] text-[var(--ink)] font-bold px-5 py-3 text-center"
            >
              View your certificate {order.final_certificate_number}
            </Link>
          )}
        </div>

        {/* Summary sidebar */}
        <aside>
          <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-5">
            <h3 className="font-serif text-xl mb-2">Order summary</h3>
            <div className="text-sm">
              <div className="mb-2"><strong>Album:</strong> {albumTitle}</div>
              {alb.completion_date && (
                <div className="mb-2 text-[var(--smoke)]">Completion date: {alb.completion_date}</div>
              )}
            </div>
            <div className="text-sm mt-3 space-y-1">
              <Line label="Certificate" cents={pricing.certificate_cents ?? 500}/>
              <Line label="Shipping" cents={pricing.shipping_cents ?? 500}/>
              <Line label="Subtotal" cents={pricing.subtotal_cents ?? 1000}/>
              <Line label="Florida sales tax (7%)" cents={pricing.tax_cents ?? 70}/>
              <div className="border-t border-[var(--ink)]/10 my-2"/>
              <Line label="Total" cents={pricing.total_cents ?? 1070} bold big data-testid="cert-status-total"/>
            </div>
            {c.first_name && (
              <div className="mt-4 text-xs text-[var(--smoke)]" data-testid="cert-status-customer-block">
                <div><strong>{c.first_name} {c.last_name}</strong></div>
                <div>{c.email}</div>
                <div>{c.phone}</div>
                {ship.line1 && (
                  <div className="mt-2">
                    {ship.line1}{ship.line2 ? `, ${ship.line2}` : ""}<br/>
                    {ship.city}, {ship.state} {ship.postal_code}<br/>
                    {ship.country}
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function StatusPill({ label, value, testid }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-3" data-testid={testid}>
      <div className="kicker kicker-black text-[10px]">{label}</div>
      <div className="text-sm font-bold mt-1">
        {String(value || "").replaceAll("_", " ")}
      </div>
    </div>
  );
}

function Line({ label, cents, bold, big }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-bold" : ""} ${big ? "text-lg" : ""}`}>
      <span>{label}</span>
      <span>{DOLLARS(cents)}</span>
    </div>
  );
}
